import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Plus,
  Edit,
  Merge,
  RefreshCw,
  Delete,
  Trash2,
} from "react-feather";
import { Button, Form, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  getDepartmentsAPI,
  deleteDepartmentAPI,
  updateDepartmentAPI,
  createDepartmentAPI,
} from "../../../api/api";
import PageHeader from "../../../Components/shared/PageHeader";
import LoadingSkeleton from "../../../Components/shared/LoadingSkeleton";
import EmptyState from "../../../Components/shared/EmptyState";
import DataTableWrapper from "../../../Components/shared/DataTableWrapper";
import StatusBadge from "../../../Components/shared/StatusBadge";
import ConfirmModal from "../../../Components/shared/ConfirmModal";
import { companyUrl } from "../../../utils/mhNav";
import styles from "./DepartmentList.module.scss";

export default function DepartmentList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newDepartmentName, setNewDepartmentName] = useState("");
  const [deleteRow, setDeleteRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getDepartmentsAPI();
      setRows(res.data || []);
      setHasLoaded(true);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateDepartment = async () => {
    if (!newDepartmentName.trim()) {
      toast.error("Department name is required");
      return;
    }
    try {
      await createDepartmentAPI({ name: newDepartmentName });
      toast.success("Department created successfully");
      setShowCreateModal(false);
      setNewDepartmentName("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    }
  };

  const handleDeleteDepartment = async () => {
    try {
      await deleteDepartmentAPI(deleteRow.departmentId);
      toast.success("Department deleted successfully");
      setDeleteRow(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    }
  };

  const columns = useMemo(
    () => [
      {
        name: "Department Name",
        selector: (r) => r.name,
        sortable: true,
        grow: 2,
        cell: (r) => <span className={styles.deptName}>{r.name}</span>,
      },
      {
        name: "Department ID",
        selector: (r) => r.departmentId,
        width: "180px",
        cell: (r) => <span className={styles.deptId}>{r.departmentId}</span>,
      },

      {
        name: "Status",
        width: "110px",
        cell: (r) => <StatusBadge status={r.status || "active"} />,
      },
      {
        name: "Created",
        selector: (r) => new Date(r.createdAt).toLocaleDateString(),
        width: "120px",
      },
      {
        name: "Actions",
        width: "140px",
        cell: (r) => (
          <div className={styles.actions}>
            <button
              className={styles.actionBtn}
              onClick={() =>
                navigate(companyUrl(`departments/${r.departmentId}`))
              }
              title="Edit department"
            >
              <Edit size={14} />
            </button>
            <button
              className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
              onClick={() => setDeleteRow(r)}
              title="Delete department"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ),
      },
    ],
    [navigate],
  );

  if (loading && !hasLoaded) {
    return (
      <div>
        <PageHeader
          title="Departments"
          breadcrumbs={[
            { label: "Company", path: companyUrl("dashboard") },
            { label: "Departments" },
          ]}
        />
        <LoadingSkeleton rows={5} cols={4} />
      </div>
    );
  }

  if (error && !hasLoaded) {
    return (
      <div>
        <PageHeader
          title="Departments"
          breadcrumbs={[
            { label: "Company", path: companyUrl("dashboard") },
            { label: "Departments" },
          ]}
        />
        <EmptyState
          icon={AlertTriangle}
          title="Could not load departments"
          description={error.message}
          actionLabel="Retry"
          onAction={load}
        />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Departments"
        breadcrumbs={[
          { label: "Company", path: companyUrl("dashboard") },
          { label: "Departments" },
        ]}
        primaryAction={{
          label: "Add Department",
          icon: Plus,
          onClick: () => setShowCreateModal(true),
        }}
        secondaryActions={[
          {
            label: "Refresh",
            icon: RefreshCw,
            onClick: load,
          },
        ]}
      />

      {!loading && rows.length === 0 ? (
        <EmptyState
          icon={AlertTriangle}
          title="No departments"
          description="Click 'Add Department' to create your first department."
          actionLabel="Add Department"
          onAction={() => setShowCreateModal(true)}
        />
      ) : (
        <DataTableWrapper
          columns={columns}
          data={rows}
          loading={loading}
          showSearch
          keyField="departmentId"
          onRowClick={(r) =>
            navigate(companyUrl(`departments/${r.departmentId}`))
          }
        />
      )}

      {/* Create Department Modal */}
      <Modal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        centered
      >
        <div className={styles.modal}>
          <div className={styles.modalHeader}>
            <h5 className={styles.modalTitle}>Create New Department</h5>
            <button
              className={styles.modalClose}
              onClick={() => setShowCreateModal(false)}
            >
              <span>×</span>
            </button>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.formField}>
              <label className={styles.fieldLabel}>Department Name *</label>
              <input
                type="text"
                className={styles.input}
                placeholder="e.g., Engineering, Sales, HR"
                value={newDepartmentName}
                onChange={(e) => setNewDepartmentName(e.target.value)}
                onKeyPress={(e) =>
                  e.key === "Enter" && handleCreateDepartment()
                }
                autoFocus
              />
            </div>
          </div>
          <div className={styles.modalFooter}>
            <button
              className={styles.btnOutline}
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </button>
            <button
              className={styles.btnPrimary}
              onClick={handleCreateDepartment}
            >
              Create Department
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={Boolean(deleteRow)}
        onClose={() => setDeleteRow(null)}
        onConfirm={handleDeleteDepartment}
        title="Delete Department?"
        message={`Are you sure you want to delete "${deleteRow?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        danger
      />
    </div>
  );
}
